// Capabilities — per-user feature flags that are orthogonal to roles.
//
// Slice RP-2a / SC-4 of docs/planning/firefly_role_context_presentation.md.
//
// Why a separate primitive from roles? Roles (`sales`, `owner`,
// `manufacturing`, `platform_dev`, ...) encode seniority + bucket
// membership; they're additive across an organization and apply to
// the user wherever they go. A capability is **orthogonal** to that
// axis: it's a per-user opt-in/opt-out for a specific responsibility
// that may or may not apply regardless of the user's roles.
//
// Storage: localStorage-backed self-toggle (RP-2b-i/ii) merged with
// DB-backed per-membership grants (SC-2 / RP-2c) via resolveCapabilities.

/**
 * The set of capability flags the app understands. §9 Q2 five flags;
 * add literals only when the first consumer lands.
 */
export type Capability =
  | "is_ads_runner"
  | "is_kb_author"
  | "is_dispatcher"
  | "is_floor_operator"
  | "is_cogs_author"
  | "is_personal_assistant";

/**
 * Every `Capability` literal the current build understands. Keep in
 * sync with the union above — used by storage parse + lock tests.
 */
export const KNOWN_CAPABILITIES: readonly Capability[] = [
  "is_ads_runner",
  "is_kb_author",
  "is_dispatcher",
  "is_floor_operator",
  "is_cogs_author",
  "is_personal_assistant",
];

/**
 * Predicate that returns `true` iff the current user holds **at
 * least one** of the supplied capabilities. Variadic for symmetry
 * with `HasRoleFn` from `sectionRegistry.tsx`.
 */
export type HasCapabilityFn = (...capabilities: Capability[]) => boolean;

/** Fail-closed default until a real capability source is wired. */
export const NO_CAPABILITIES: HasCapabilityFn = () => false;

export function hasCapabilityFromSet(
  granted: ReadonlySet<string>,
): HasCapabilityFn {
  return (...capabilities: Capability[]) =>
    capabilities.some((c) => granted.has(c));
}

export type CapabilityDescription = {
  id: Capability;
  label: string;
  blurb: string;
};

export const CAPABILITY_DESCRIPTIONS: readonly CapabilityDescription[] = [
  {
    id: "is_ads_runner",
    label: "I run ads for this organization",
    blurb:
      "When on, your daily checklist includes ad-logging tasks and ads-runner achievements track your activity. Off for owners / admins who delegate ads and sales reps who only cover walk-ins.",
  },
  {
    id: "is_kb_author",
    label: "I author and maintain knowledge-base content",
    blurb:
      "When on, manager-bucket surfaces include KB-gap reminders and authoring tasks. Trainers default on; owners and admins turn this on when they personally curate articles.",
  },
  {
    id: "is_dispatcher",
    label: "I dispatch routes and coordinate deliveries",
    blurb:
      "When on, logistics-bucket firefly surfaces route and stop attention signals. Logistics coordinators default on; desk manufacturing or admin users opt in when they run dispatch.",
  },
  {
    id: "is_floor_operator",
    label: "I work on the production floor",
    blurb:
      "When on, production-bucket surfaces include station and consumption reminders. Desk-side manufacturing users leave this off.",
  },
  {
    id: "is_cogs_author",
    label: "I own COGS and period-close for manufacturing",
    blurb:
      "When on, manufacturing-bucket surfaces include period-close and variance reminders. Opt in for the person who closes books — not every shop-floor user.",
  },
  {
    id: "is_personal_assistant",
    label: "I use Firefly Personal Assistant mode",
    blurb:
      "When on, you can turn on the multi-turn Personal Assistant inside Firefly to capture notes and run bounded actions. Owners default on; admins opt in when they act as an executive assistant.",
  },
];
